import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";

export type AuditInconsistencyType =
    | "MISSING_REASON"
    | "INVALID_REASON"
    | "EXPIRED_RULE"
    | "FINE_MISMATCH"
    | "SUSPENSION_MATCHES_MISMATCH"
    | "SUSPENSION_DURATION_MISMATCH"
    | "SUSPENSION_TYPE_MISMATCH"
    | "YELLOW_CARD_WITH_SUSPENSION"
    | "RED_CARD_WITHOUT_SUSPENSION"
    | "INVALID_DATE_RANGE"
    | "NEGATIVE_REMAINING_MATCHES"
    | "DUPLICATE_SANCTION_SAME_MATCH";

export type AuditInconsistencySeverity = "critical" | "warning" | "info";

export interface AuditInconsistencyServerPayload {
    id: string;
    sanctionId: string;
    type: AuditInconsistencyType;
    severity: AuditInconsistencySeverity;
    message: string;
    expectedValue?: string | number | null;
    actualValue?: string | number | null;
    isAutoFixable?: boolean;
    autoFixData?: unknown;
    affectedPlayers?: string[];
    affectedMatches?: string[];
    duplicateSanctionIds?: string[];
    duplicateGroupKey?: string;
}

interface ListResponse {
    totalCount: number;
    items: AuditInconsistencyServerPayload[];
}

const AUDIT_FN_NAME = "disciplinary-audit-inconsistencies";
const STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Types comptés dans le badge de l'onglet Règles — ceux qu'un opérateur doit
 * corriger. MISSING_REASON / INVALID_REASON restent dans la liste mais sont
 * informatifs (import FAF sans motif, motif désactivé depuis) ; les écarts de
 * barème (FINE_MISMATCH…) sont des avertissements. Le badge est dérivé des
 * items, jamais de `total_count` : le cache frais (toute la liste, 109 en
 * 2025-2026) et le recalcul (0) affichaient deux nombres différents.
 */
const BADGE_TYPES: ReadonlySet<AuditInconsistencyType> = new Set<AuditInconsistencyType>([
    "YELLOW_CARD_WITH_SUSPENSION",
    "RED_CARD_WITHOUT_SUSPENSION",
    "INVALID_DATE_RANGE",
    "DUPLICATE_SANCTION_SAME_MATCH",
]);

export function isBadgeInconsistency(item: Pick<AuditInconsistencyServerPayload, "type">): boolean {
    return BADGE_TYPES.has(item.type);
}

export function countBadgeInconsistencies(items: Pick<AuditInconsistencyServerPayload, "type">[]): number {
    return items.filter(isBadgeInconsistency).length;
}

/** Fire-and-forget: write EF result back to cache table via RPC */
function writeAuditCache(leagueId: string, season: string, totalCount: number, items: unknown[]) {
    supabase.rpc("upsert_audit_cache", {
        p_league_id: leagueId,
        p_season: season,
        p_total_count: totalCount,
        p_items: items,
    }).then(({ error }) => {
        if (error) console.warn("[audit-cache] write failed:", error.message);
    });
}

/** Cache frais sinon recalcul `list` (écrit dans le cache). */
async function fetchAuditList(leagueId: string, season: string, matchLeagueIds?: string[]): Promise<ListResponse> {
    // Try cache table first (instant, <50ms)
    const { data: cached } = await supabase
        .from("disciplinary_audit_cache")
        .select("total_count, items, stale")
        .eq("league_id", leagueId)
        .eq("season", season)
        .maybeSingle();

    if (cached && !cached.stale) {
        return {
            totalCount: cached.total_count as number,
            items: Array.isArray(cached.items) ? cached.items : [],
        } as ListResponse;
    }

    // Cache miss or stale — fall back to EF call
    const { data, error } = await supabase.functions.invoke(AUDIT_FN_NAME, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: {
            action: "list",
            leagueId,
            season,
            matchLeagueIds: matchLeagueIds && matchLeagueIds.length > 0 ? matchLeagueIds : undefined,
            clientNowIso: new Date().toISOString(),
        },
    });

    if (error) throw error;

    const res = data as ListResponse;
    const items = Array.isArray(res?.items) ? res.items : [];
    const result = { totalCount: items.length, items } as ListResponse;

    // Write result back to cache (fire-and-forget)
    writeAuditCache(leagueId, season, result.totalCount, result.items);

    return result;
}

/** Racine des clés compte + liste de l'audit ; invalidée par le temps réel à chaque changement de `sanctions`. */
export const DISCIPLINARY_AUDIT_QUERY_KEY_PREFIX = "disciplinaryAudit";

export function useDisciplinaryAuditCount(params: {
    leagueId?: string;
    season?: string;
    matchLeagueIds?: string[];
    enabled?: boolean;
}) {
    const { leagueId, season, matchLeagueIds, enabled = true } = params;

    return useQuery({
        queryKey: [DISCIPLINARY_AUDIT_QUERY_KEY_PREFIX, "count", { leagueId, season, matchLeagueIds }],
        queryFn: async () => {
            if (!leagueId || !season) return 0;
            const { items } = await fetchAuditList(leagueId, season, matchLeagueIds);
            return countBadgeInconsistencies(items);
        },
        enabled: enabled && !!leagueId && !!season,
        staleTime: STALE_TIME,
    });
}

export function useDisciplinaryAuditInconsistencies(params: {
    leagueId?: string;
    season?: string;
    matchLeagueIds?: string[];
    enabled?: boolean;
}) {
    const { leagueId, season, matchLeagueIds, enabled = true } = params;

    return useQuery({
        queryKey: [DISCIPLINARY_AUDIT_QUERY_KEY_PREFIX, "list", { leagueId, season, matchLeagueIds }],
        queryFn: async () => {
            if (!leagueId || !season) return { totalCount: 0, items: [] } as ListResponse;
            return fetchAuditList(leagueId, season, matchLeagueIds);
        },
        enabled: enabled && !!leagueId && !!season,
        staleTime: STALE_TIME,
    });
}
